// src/components/Sandbox/QuantumAlgorithmSandbox.jsx
import React, { useEffect, useState, useRef } from "react";

import "./QuantumAlgorithmsSandbox.css";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import {
  zeroState,
  H,
  X,
  applySingleQubitGate,
  applyCNOT,
  applyCZ,
  measureProbabilities,
} from "../../utils/quantum";

import QuantumAccessPopup from "../Quantum/QuantumAccessPopup";

/**
 * Small demo algorithm presets (0-based qubit indices).
 * Keep them conservative so they work with 2-5 qubits.
 */
const ALGORITHMS = [
  {
    id: "shor-simplified",
    name: "Shor (simplified)",
    description:
      "Simplified Shor-like sequence illustrating multi-qubit entanglement and gates.",
    recommendedQubits: 4,
    getGates: (nQ) => {
      const gates = [];
      for (let i = 0; i < Math.min(2, nQ); i++)
        gates.push({ gate: "H", target: i });
      if (nQ >= 3) gates.push({ gate: "CNOT", control: 0, target: 2 });
      if (nQ >= 4) gates.push({ gate: "CNOT", control: 1, target: 3 });
      if (nQ >= 4) gates.push({ gate: "CZ", control: 2, target: 3 });
      return gates;
    },
  },
  {
    id: "deutsch-jozsa",
    name: "Deutsch–Jozsa",
    description:
      "Detects whether a given 1-bit function is constant or balanced using quantum parallelism.",
    recommendedQubits: 2,
    getGates: (nQ) => {
      const gates = [];
      for (let i = 0; i < nQ; i++) gates.push({ gate: "H", target: i });
      if (nQ >= 2) gates.push({ gate: "CNOT", control: 0, target: nQ - 1 });
      for (let i = 0; i < nQ - 1; i++) gates.push({ gate: "H", target: i });
      return gates;
    },
  },
  {
    id: "grover-demo",
    name: "Grover",
    description: "Toy Grover marking |11..1〉 with CZ.",
    recommendedQubits: 2,
    getGates: (nQ) => {
      const gates = [];
      for (let i = 0; i < nQ; i++) gates.push({ gate: "H", target: i });
      if (nQ >= 2) gates.push({ gate: "CZ", control: 0, target: nQ - 1 });
      for (let i = 0; i < nQ; i++) gates.push({ gate: "H", target: i });
      return gates;
    },
  },
  {
    id: "qft-demo",
    name: "QFT (small)",
    description: "Simplified N-qubit Quantum Fourier Transform example.",
    recommendedQubits: 3,
    getGates: (nQ) => {
      const gates = [];
      for (let i = 0; i < nQ; i++) gates.push({ gate: "H", target: i });
      for (let i = 0; i < nQ - 1; i++)
        gates.push({ gate: "CZ", control: i, target: i + 1 });
      return gates;
    },
  },
];

/* ---------------------- Helpers (local) ---------------------- */

/* complex helpers for state amplitudes stored as {re, im} */
const cAdd = (a, b) => ({ re: a.re + b.re, im: a.im + b.im });
const cMul = (a, b) => ({
  re: a.re * b.re - a.im * b.im,
  im: a.re * b.im + a.im * b.re,
});
const cConj = (a) => ({ re: a.re, im: -a.im });

/*
  compute 2x2 reduced density matrix for qubit `q` (0-based, q=0 is MSB)
  state: array of complex amps length 2^n
  returns: [[rho00, rho01],[rho10, rho11]] each complex {re,im}
*/
function reducedDensityMatrixForQubit(state, q) {
  const n = Math.round(Math.log2(state.length));
  const rho = [
    [
      { re: 0, im: 0 },
      { re: 0, im: 0 },
    ],
    [
      { re: 0, im: 0 },
      { re: 0, im: 0 },
    ],
  ];

  // iterate over full indices, accumulate psi_{...i...} psi*_{...j...} for the qubit's local indices
  for (let idx = 0; idx < state.length; idx++) {
    const bit = (idx >> (n - q - 1)) & 1; // value (0 or 1) of qubit q
    const amp = state[idx];
    // sum over j where j has same other bits but target qubit value = jBit
    // we can pair idx with idx' that differs only in qubit q
    // but general approach: for each idx and jdx that differ only in q's bit
    for (let jdx = 0; jdx < state.length; jdx++) {
      // check if other bits equal
      const otherMask = ~(1 << (n - q - 1));
      // Instead of complex mask trick, compare bits except q:
      let same = true;
      for (let bitPos = 0; bitPos < n; bitPos++) {
        if (bitPos === n - q - 1) continue;
        const bi = (idx >> bitPos) & 1;
        const bj = (jdx >> bitPos) & 1;
        if (bi !== bj) {
          same = false;
          break;
        }
      }
      if (!same) continue;
      const jbit = (jdx >> (n - q - 1)) & 1;
      // add amp(idx) * conj(amp(jdx)) to rho[bit][jbit]
      const contrib = cMul(amp, cConj(state[jdx]));
      rho[bit][jbit] = cAdd(rho[bit][jbit], contrib);
    }
  }
  return rho;
}

/* convert 2x2 density matrix (complex) to Bloch vector r = (rx,ry,rz)
   rx = Tr(rho X), ry = Tr(rho Y), rz = Tr(rho Z)
   Pauli matrices:
   X = [[0,1],[1,0]] , Y = [[0,-i],[i,0]], Z = [[1,0],[0,-1]]
*/
function blochFromRho(rho) {
  // rho: [[00,01],[10,11]]
  const rho00 = rho[0][0];
  const rho01 = rho[0][1];
  const rho10 = rho[1][0];
  const rho11 = rho[1][1];

  // Tr(rho X) = rho01 + rho10  (these are complex, but Bloch components are real)
  const rx = rho01.re + rho10.re;
  // Tr(rho Y) = -i rho01 + i rho10 = (-i)*(a+ib) + i*(c+id) -> real = (rho10.im - rho01.im)
  const ry = rho10.im - rho01.im;
  // Tr(rho Z) = rho00 - rho11
  const rz = rho00.re - rho11.re;

  // clamp to [-1,1] small numeric rounding possible
  const clamp = (v) => Math.max(-1, Math.min(1, v));
  return [clamp(rx), clamp(ry), clamp(rz)];
}

/* apply gate object to state (pure state amplitudes) */
function applyGateToState(st, nQ, g) {
  if (!g) return st;
  if (g.gate === "H") return applySingleQubitGate(st, nQ, g.target, H);
  if (g.gate === "X") return applySingleQubitGate(st, nQ, g.target, X);
  if (g.gate === "CNOT") return applyCNOT(st, g.control, g.target);
  if (g.gate === "CZ") return applyCZ(st, g.control, g.target);
  // unknown -> return same
  return st;
}

/* sample counts from distribution (probs array) for shots */
function sampleCountsFromProbs(probs, shots) {
  const counts = new Array(probs.length).fill(0);
  // create cumulative
  const cum = [];
  let s = 0;
  for (let p of probs) {
    s += p;
    cum.push(s);
  }
  for (let i = 0; i < shots; i++) {
    const r = Math.random();
    // find index
    let idx = cum.findIndex((c) => r <= c);
    if (idx === -1) idx = probs.length - 1;
    counts[idx] += 1;
  }
  return counts;
}

/* -------------------- Component -------------------- */

export default function QuantumAlgorithmSandbox({
  maxQubits = 15, // default up to 5 for demo; independent module
}) {
  const [nQ, setNQ] = useState(4);
  const [selectedAlgId, setSelectedAlgId] = useState(ALGORITHMS[0].id);
  const [algorithm, setAlgorithm] = useState(ALGORITHMS[0]);
  const [queue, setQueue] = useState([]); // loaded gate list
  const [state, setState] = useState(zeroState(2));
  const [stepIndex, setStepIndex] = useState(0);
  const [activeStep, setActiveStep] = useState(null); // for animation
  const [probs, setProbs] = useState(measureProbabilities(zeroState(2)));
  const [counts, setCounts] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const alg = ALGORITHMS.find((a) => a.id === selectedAlgId) || ALGORITHMS[0];
    setAlgorithm(alg);
  }, [selectedAlgId]);

  useEffect(() => {
    const alg = ALGORITHMS.find((a) => a.id === selectedAlgId) || ALGORITHMS[0];
    setAlgorithm(alg);

    // auto-generate gates for new nQ
    const gates = alg.getGates ? alg.getGates(nQ) : alg.gates || [];
    setQueue(gates);
    setState(zeroState(nQ));
    setStepIndex(0);
    setActiveStep(null);
    setCounts(null);
    setProbs(measureProbabilities(zeroState(nQ)));
  }, [nQ, selectedAlgId]);

  useEffect(() => {
    setProbs(measureProbabilities(state));
  }, [state]);

  // const loadAlgorithm = () => {
  //   const validated = (algorithm.gates || []).filter((g) => {
  //     if (g.target != null && (g.target < 0 || g.target >= nQ)) return false;
  //     if (g.control != null && (g.control < 0 || g.control >= nQ)) return false;
  //     return true;
  //   });
  //   setQueue(validated);
  //   setState(zeroState(nQ));
  //   setStepIndex(0);
  //   setActiveStep(null);
  //   setCounts(null);
  // };

  const loadAlgorithm = () => {
    const gates = algorithm.getGates
      ? algorithm.getGates(nQ)
      : algorithm.gates || [];
    setQueue(gates);
    setState(zeroState(nQ));
    setStepIndex(0);
    setActiveStep(null);
    setCounts(null);
  };

  const stepOnce = async (withAnimation = true) => {
    if (stepIndex >= queue.length) return;
    const g = queue[stepIndex];
    if (withAnimation) {
      setActiveStep(stepIndex);
      // small delay to show animation
      await new Promise((res) => setTimeout(res, 420));
    }
    const newState = applyGateToState(state, nQ, g);
    setState(newState);
    setStepIndex((s) => s + 1);
    if (withAnimation) {
      // keep highlight briefly
      await new Promise((res) => setTimeout(res, 420));
      setActiveStep(null);
    }
  };

  const runAll = () => {
    let st = state;
    for (let i = stepIndex; i < queue.length; i++) {
      st = applyGateToState(st, nQ, queue[i]);
    }
    setState(st);
    setStepIndex(queue.length);
    setActiveStep(null);
  };

  const runShots = (shots = 100) => {
    const ps = measureProbabilities(state);
    const c = sampleCountsFromProbs(ps, shots);
    setCounts(c);
  };

  const resetAll = () => {
    setState(zeroState(nQ));
    setQueue([]);
    setStepIndex(0);
    setActiveStep(null);
    setCounts(null);
  };

  // compute exact reduced Bloch for each qubit
  const blochs = (() => {
    if (!state) return [];
    const n = Math.round(Math.log2(state.length));
    const out = [];
    for (let q = 0; q < n; q++) {
      const rho = reducedDensityMatrixForQubit(state, q);
      out.push(blochFromRho(rho));
    }
    return out;
  })();

  // timeline label helper
  const timelineLabel = (g) => {
    if (!g) return "";
    if (g.gate === "CNOT" || g.gate === "CZ")
      return `${g.gate}  c${(g.control || 0) + 1} → t${(g.target || 0) + 1}`;
    return `${g.gate}  t${(g.target || 0) + 1}`;
  };

  function BlochArrow({ vec }) {
    const arrowRef = useRef();
    const tipRef = useRef();
    useFrame(() => {
      if (!vec || !arrowRef.current) return;
      const mapped = new THREE.Vector3(vec[0], vec[2], -vec[1]).normalize();
      arrowRef.current.setDirection(mapped);
      arrowRef.current.setLength(1.1);
      if (tipRef.current) {
        tipRef.current.position.set(
          mapped.x * 1.1,
          mapped.y * 1.1,
          mapped.z * 1.1
        );
      }
    });
    return (
      <>
        <primitive
          ref={arrowRef}
          object={
            new THREE.ArrowHelper(
              new THREE.Vector3(0, 1, 0),
              new THREE.Vector3(0, 0, 0),
              1.1,
              0x00ff00
            )
          }
        />
        <mesh ref={tipRef}>
          <sphereGeometry args={[0.05, 16, 16]} />
          <meshBasicMaterial color="#ffff00" />
        </mesh>
      </>
    );
  }

  function BlochCanvas({ vec }) {
    if (!vec) return <div className="bloch-placeholder">—</div>;
    return (
      <Canvas
        style={{ width: 200, height: 200 }}
        camera={{ position: [0, 0, 1.7] }}
      >
        <ambientLight intensity={0.5} />
        <pointLight position={[5, 5, 5]} />
        <mesh>
          <sphereGeometry args={[1, 32, 32]} />
          <meshBasicMaterial color="#0ff" wireframe />
        </mesh>
        <BlochArrow vec={vec} />
        <OrbitControls enableZoom={true} />
      </Canvas>
    );
  }

  return (
    <div className="qc-sim-root" style={{}}>
      <div className="qc-header-wrapper">
        <h2
          style={{
            marginTop: 5,
            display: "inline-block",
            borderBottom: "2px solid #0f0",
            paddingBottom: 6,
          }}
        >
          Quantum Algorithms Sandbox
        </h2>
        <div className="qc-description">
          Explore quantum algorithms in action — watch gates and Bloch spheres
          come alive.
        </div>
      </div>

      {showPopup && <QuantumAccessPopup onClose={() => setShowPopup(false)} />}

      <div className="qc-sim-container">
        <div className="qc-sim-box full-width-box">
          {/* Top controls */}
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <label style={{ fontWeight: "bold", color: "var(--neon-green)" }}>
              Qubits:
              <select
                className="lab-select"
                value={nQ}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val > 4) {
                    setShowPopup(true);
                  } else {
                    setNQ(val);
                  }
                }}
              >
                {Array.from({ length: maxQubits - 1 }, (_, i) => i + 2).map(
                  (n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  )
                )}
              </select>
            </label>

            <label style={{ fontWeight: "bold", color: "var(--neon-green)" }}>
              Algorithm:
              <select
                className="lab-select"
                value={selectedAlgId}
                onChange={(e) => setSelectedAlgId(e.target.value)}
                style={{ marginLeft: 8 }}
              >
                {ALGORITHMS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>

            <button className="lab-btn" onClick={loadAlgorithm}>
              Load Algorithm
            </button>
            <button
              className="lab-btn"
              onClick={() => stepOnce(true)}
              disabled={stepIndex >= queue.length}
            >
              Step
            </button>
            <button
              className="lab-btn"
              onClick={runAll}
              disabled={queue.length === 0 || stepIndex >= queue.length}
            >
              Run All
            </button>
            <button className="lab-btn" onClick={resetAll}>
              Clear
            </button>
          </div>

          <div style={{ textAlign: "center", marginTop: 5 }}>
            <small style={{ color: "var(--neon-green)" }}>
              Algorithm: {algorithm?.description || ""}
            </small>
          </div>

          <div
            className="timeline"
            style={{
              display: "flex",
              gap: 16,
              justifyContent: "center",
              flexWrap: "wrap", // inline/horizontal
              alignItems: "flex-start",

              // marginTop: 8,
            }}
          >
            {queue.length === 0 && (
              <div style={{ color: "#0a0" }}>
                No gates loaded. Click "Load Algorithm".
              </div>
            )}
            {queue.map((g, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: 140,
                  opacity: i < stepIndex ? 0.7 : 1,
                  transition: "transform 0.3s, opacity 0.3s",
                }}
              >
                <div
                  style={{
                    border: "1px dashed var(--neon-cyan)", // blue dotted border
                    borderRadius: 8,
                    minWidth: 140,
                    padding: "12px 8px",
                    background: "#000",
                    textAlign: "center",
                    boxShadow:
                      activeStep === i ? "0 0 12px var(--neon-cyan)" : "none",
                    transition: "0.3s",
                  }}
                >
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      color: "var(--neon-cyan)",
                    }}
                  >
                    {g.gate}
                  </div>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--neon-green)",
                      marginTop: 4,
                    }}
                  >
                    {timelineLabel(g)}
                  </div>
                </div>
                <div style={{ marginTop: 6 }}>
                  {i < stepIndex
                    ? "applied ✓"
                    : i === stepIndex
                    ? "step →"
                    : ""}
                </div>
              </div>
            ))}
          </div>

          <div style={{ textAlign: "center", marginTop: 12 }}>
            <small style={{ color: "var(--neon-green)" }}>
              Tip: Click "Step" to apply gates one by one, Click "Run All" to
              run all gates.
            </small>
          </div>
        </div>

        <div className="qc-row qc-blochs">
          <div className="bloch-grid">
            {blochs.slice(0, nQ).map((vec, i) => (
              <div className="bloch-card visual" key={i}>
                <h5>Qubit {i + 1}</h5>
                <BlochCanvas vec={vec} />
              </div>
            ))}
          </div>
        </div>

        <br></br>
      </div>

      {/* <div className="visual" style={{ padding: 12 }}>
                <h4 style={{ marginTop: 0 }}>{algorithm.name}</h4>
                <div style={{ color: "#0ff", marginBottom: 8 }}>
                  {algorithm.description}
                </div>
                <div style={{ color: "var(--neon-green)" }}>
                  Recommended Qubits: {algorithm.recommendedQubits}
                </div>
              </div> */}

      <div
        style={{
          display: "flex",
          gap: 50,
          justifyContent: "center",
          marginTop: 40,
          flexWrap: "wrap", // allows responsive wrapping
        }}
      >
        {/* Column 1: Probability Distribution */}
        <div
          className="visual"
          style={{ flex: 1, minWidth: 250, maxWidth: 400 }}
        >
          <h4 style={{ marginTop: 0 }}>Probability Distribution</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{ border: "1px solid var(--neon-green)", padding: 6 }}
                >
                  State
                </th>
                <th
                  style={{ border: "1px solid var(--neon-green)", padding: 6 }}
                >
                  Probability
                </th>
              </tr>
            </thead>
            <tbody>
              {probs.map((p, idx) =>
                p > 1e-6 ? (
                  <tr key={idx}>
                    <td
                      style={{
                        border: "1px solid var(--neon-green)",
                        padding: 6,
                      }}
                    >
                      |{idx.toString(2).padStart(nQ, "0")}〉
                    </td>
                    <td
                      style={{
                        border: "1px solid var(--neon-green)",
                        padding: 6,
                      }}
                    >
                      {(p * 100).toFixed(3)}%
                    </td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>
        </div>

        {/* Column 2: Measurement Counts */}
        <div
          className="visual"
          style={{ flex: 1, minWidth: 250, maxWidth: 400 }}
        >
          <h4 style={{ marginTop: 0 }}>Measurement Counts (Run Shots)</h4>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <input
              type="number"
              min={1}
              max={100000}
              defaultValue={100}
              id="sandbox-shots-input"
              style={{
                background: "#000",
                color: "var(--neon-green)",
                border: "1px solid var(--neon-green)",
                padding: "6px",
                width: 120,
              }}
            />
            <button
              className="lab-btn"
              onClick={() => {
                const el = document.getElementById("sandbox-shots-input");
                const shots = Number(el?.value || 100);
                runShots(shots);
              }}
            >
              Run Shots
            </button>
          </div>

          <div style={{ marginTop: 10 }}>
            {counts ? (
              <div className="counts">
                <table>
                  <thead>
                    <tr>
                      <th>State</th>
                      <th>Counts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {counts.map((c, i) =>
                      c > 0 ? (
                        <tr key={i}>
                          <td>|{i.toString(2).padStart(nQ, "0")}〉</td>
                          <td>{c}</td>
                        </tr>
                      ) : null
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="muted" style={{ textAlign: "center" }}>
                No counts yet — run shots to see distribution.
              </div>
            )}
          </div>
        </div>

        {/* <div
          className="visual"
          style={{ flex: 1, minWidth: 250, maxWidth: 400 }}
        >
          <h4 style={{ marginTop: 0 }}>Equations</h4>
          <div style={{ color: "var(--neon-green)", fontSize: 14 }}>
            
            {algorithm && algorithm.gates? (
              <div>
                <p>
                  <strong>{algorithm.name}:</strong>
                </p>
                <p>{algorithm.description}</p>
                <p>
                  Gate sequence:{" "}
                  {algorithm.gates.map((g) => g.gate).join(" → ")}
                </p>
              </div>
            ) : (
              <p>No algorithm loaded</p>
            )}
          </div>
        </div>
      
       */}
      </div>
    </div>
  );
}
